import classNames from 'classnames';
import ProgressBar from 'components/ProgressBar';
import ShareButtons from 'components/shareButtons';
import PcModelStatTip from 'containers/PcModel/PcModelStatTip';
import calculatePrice, { calculateComparePrice } from 'helpers/calculatePrice';
import { calculateScore } from 'helpers/calculateScore';
import {
  getLookAndFeelIcon,
  getlookFeelPerformance,
} from 'helpers/getLookAndFeel';
import Image from 'next/image';
import PropTypes from 'prop-types';
import { useState } from 'react';
import ReactModal from 'react-modal';
import Scrollbar from 'react-scrollbars-custom';
import { JsonParam, useQueryParam, withDefault } from 'use-query-params';
import { usePcConfigurator } from 'utils/PcConfiguratorContext';
import PcModelDelayModal from '../PcModelDelayModal';
import PcModelModalHelp from '../PcModelModalHelp';
import PcModelOverviewSpecs from '../PcModelOverviewSpecs';
import PcPerformanceTips from '../PcModelPerformanceTips';
import PcModelRemovedItemModal from '../PcModelRemovedItemModal';
import PcModelStepsNext from '../PcModelStepsNext';
import PcModelSurveyBox from '../PcModelSurveyBox';
import PcModelValidationModal from '../PcModelValidationModal';
import styles from './styles';
import PcModelSaleHighlightsMobile from '../PcModeSaleHighlightsMobile';

const LOOK_AND_FEEL = {
  light_it_up: 'LIGHT IT UP',
  in_between: 'IN BETWEEN',
  refined: 'REFINED',
};

const PcModelOverview = ({ productType, sale_highlights }) => {
  const { pcModel, currentOptions, errors, pcConfigDoc } = usePcConfigurator();

  const [target] = useQueryParam('target', withDefault(JsonParam, {}));
  const [currentSelections] = useQueryParam('selections', JsonParam);
  const removedOptions = [];
  if (currentSelections) {
    Object.keys(currentSelections).forEach((current) => {
      const selectedOption = currentOptions.find(
        (o) => o.group_type === current,
      );
      if (!selectedOption) {
        removedOptions.push(current);
      }
    });
  }

  const gpuRes = ['1080p', '1440p', '4k'];
  const selectGPURes = () => {
    if (target.score_gpu_1080) return gpuRes[0];
    if (target.score_gpu_1440) return gpuRes[1];
    if (target.score_gpu_4k) return gpuRes[2];
    return gpuRes[0];
  };
  const [selectedGPURes, setSelectedGPURes] = useState(selectGPURes());

  const [showResSelectorDropdown, setShowResSelectorDropdown] = useState(false);

  const toggleResSelectorDropdown = () => {
    setShowResSelectorDropdown(!showResSelectorDropdown);
  };

  const gpu1080Score = calculateScore(currentOptions, 'score_gpu_1080');
  const gpu1440Score = calculateScore(currentOptions, 'score_gpu_1440');
  const gpu4kScore = calculateScore(currentOptions, 'score_gpu_4k');
  const cpuScore = calculateScore(currentOptions, 'score_cpu');
  const ramScore = calculateScore(currentOptions, 'score_ram');
  const ssdScore = calculateScore(currentOptions, 'score_ssd');
  const rgbScore = calculateScore(currentOptions, 'score_rgb');

  const performanceStats = [];

  performanceStats.push({
    name: 'gpu_1080',
    title: pcConfigDoc?.data?.gpu_1080_title || 'Graphic Performance',
    value: gpu1080Score,
    target: target.score_gpu_1080 || null,
  });

  performanceStats.push({
    name: 'gpu_1440',
    title: pcConfigDoc?.data?.gpu_1440_title || 'Graphic Performance',
    value: gpu1440Score,
    target: target.score_gpu_1440 || null,
  });

  performanceStats.push({
    name: 'gpu_4k',
    title: pcConfigDoc?.data?.gpu_4k_title || 'Graphic Performance',
    value: gpu4kScore,
    target: target.score_gpu_4k || null,
  });

  performanceStats.push({
    name: 'cpu',
    title: pcConfigDoc?.data?.cpu_title || 'Processing Speed',
    value: cpuScore,
    target: target.score_cpu || null,
  });

  performanceStats.push({
    name: 'ram',
    title: pcConfigDoc?.data?.ram_title || 'RAM Size/Speed',
    value: ramScore,
    target: target.score_ram || null,
  });
  performanceStats.push({
    name: 'ssd',
    title: pcConfigDoc?.data?.ssd_title || 'Application Load Speed',
    value: ssdScore,
    target: target.score_ssd || null,
  });
  const lookFeelSelected =
    rgbScore < 6.66
      ? 'refined'
      : rgbScore < 13.33
      ? 'in_between'
      : 'light_it_up';

  const updateRes = (res) => {
    setSelectedGPURes(res);
    const value = performanceStats?.find((stat) =>
      stat?.name?.includes('gpu'),
    )?.value;
    switch (res) {
      case '1080p':
        target.score_gpu_1080 = value;
        break;
      case '1440p':
        target.score_gpu_1440 = value;
        break;
      case '4k':
        target.score_gpu_4k = value;
        break;
      default:
        target.score_gpu_4k = value;
        break;
    }
  };

  const [showShare, setShowShare] = useState(false);
  const toggleShareConfig = () => {
    setShowShare(!showShare);
  };
  const price = calculatePrice(currentOptions, pcModel.price);

  const comparePrice = calculateComparePrice(
    currentOptions,
    pcModel.compare_at,
  );
  const [isOverviewExpanded, setIsOverviewExpanded] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const needHelpTitle = pcConfigDoc.data.need_help_title;

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [tipName, setTipName] = useState(null);

  const handleOpenRgbModal = () => {
    setIsTooltipOpen(true);
    setTipName('rgb');
  };

  const handleCloseModal = () => {
    setIsTooltipOpen(false);
  };

  const handleClose = () => {
    setShowShare(false);
  };

  const [isTipOpen, setIsTipOpen] = useState(false);

  const handleCloseTip = () => {
    setIsTipOpen(false);
  };

  const handleOpenTip = () => {
    setIsTipOpen(true);
  };

  const lookAndFeelIcon = getLookAndFeelIcon(rgbScore, target.score_rgb);

  const lookFeelPerformance = getlookFeelPerformance(
    rgbScore,
    target.score_rgb,
  );

  return (
    <div className='pc-model-overview-container'>
      <div className='pc-model-overview'>
        <div className='pc-model-overview-summary'>
          {pcModel && (
            <div className='pc-model-overview-image'>
              <Image
                draggable={false}
                src={
                  pcModel?.thumb_image ||
                  'https://cdn.shopify.com/s/files/1/0637/0407/2436/files/image-placeholder.png?v=1714058007'
                }
                alt={pcModel?.model_title ?? 'Image'}
                width={120}
                height={120}
              />
            </div>
          )}

          <div className='pc-model-overview-info'>
            {/* <div className="pc-model-overview-header">
              {doc?.data?.title}&#39;s performance
            </div>
            <div className="pc-model-overview-as-configured">As configured</div>
            <div className="pc-model-overview-show-current">
              <PcModelOverviewSpecs productType={productType} />
            </div> */}
            <div className='pc-model-overview-header'>
              {pcModel.model_title}&#39;s performance
            </div>
            {/* <div className="pc-model-overview-as-configured">As configured</div> */}
            <div className='pc-model-overview-show-current'>
              <PcModelOverviewSpecs productType={productType} />
            </div>
          </div>
          {/* <div className="pc-model-overview-pricing">
            <div className="pc-model-overview-money">
              <span className="pc-model-overview-currency">SGD</span>
              <span className="pc-model-overview-amount">
                <span>${parseFloat(price).toLocaleString()}</span>
              </span>
            </div>
            <div>
              {parseFloat(comparePrice) - parseFloat(price) > 0 && (
                <div className="compare-container">
                  <span className="compare_price">
                    <del>${parseFloat(comparePrice).toLocaleString()}</del>
                  </span>
                  <span className="saved_money">
                    You save ${parseFloat(comparePrice) - parseFloat(price)}
                  </span>
                </div>
              )}
            </div>
          </div> */}
        </div>
        <div className='add-to-cart-alerts'>
          <PcModelValidationModal errors={errors} />
          <PcModelDelayModal currentOptions={currentOptions} />
          <PcModelRemovedItemModal
            currentOptions={currentOptions}
            removedOptions={removedOptions}
          />
        </div>

        {!isOverviewExpanded && (
          <div className='pc-model-overview-summary-mobile'>
            <div className='pc-model-overview-info'>
              <a
                className='pc-model-overview-mobile-heading'
                role='button'
                onClick={() => setIsOverviewExpanded(!isOverviewExpanded)}
              >
                <span className='performance-icon'>
                  {isOverviewExpanded ? (
                    <Image
                      draggable={false}
                      src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/ChevronDown.svg?v=1714034100'
                      alt='Hide Performance'
                      width={16}
                      height={16}
                    />
                  ) : (
                    <Image
                      draggable={false}
                      src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/ChevronUp.svg?v=1714034075'
                      alt='Hide Performance'
                      width={16}
                      height={16}
                    />
                  )}
                </span>
                <span className='performance'>
                  {!isOverviewExpanded && 'MORE DETAILS'}
                </span>
              </a>
              {/* <div className='pc-model-overview-mobile-heading'>
                <a
                  role='button'
                  onClick={() => setIsOverviewExpanded(!isOverviewExpanded)}
                >
                  {isOverviewExpanded ? 'Hide Performance' : 'Show Performance'}
                </a>
              </div> */}
              <div className='pc-model-overview-money'>
                {/* <span className="pc-model-overview-currency">
                  AS CONFIGURED
                </span> */}

                <span className='pc-model-overview-currency'>
                  <div>
                    {parseFloat(comparePrice) - parseFloat(price) > 0 && (
                      <div className='compare-container'>
                        <div className='compare_price'>
                          <del>
                            ${parseFloat(comparePrice).toLocaleString()}
                          </del>
                        </div>
                        <div className='saved_money'>
                          You save $
                          {parseFloat(comparePrice) - parseFloat(price)}
                        </div>
                      </div>
                    )}
                  </div>
                </span>
                <span className='pc-model-overview-amount'>
                  <span>${parseFloat(price).toLocaleString()}</span>
                </span>
              </div>
            </div>
          </div>
        )}

        <div className='pc-model-overview-stats-desktop'>
          <div className='pc-model-overview-stats'>
            {performanceStats.map((performanceStat) => {
              if (
                performanceStat.name.includes('gpu') &&
                !performanceStat.name.includes(selectedGPURes.replace('p', ''))
              )
                return null;
              const handleOpenModal = () => {
                setIsTooltipOpen(true);
                setTipName(performanceStat.name);
              };

              let { title } = performanceStat;
              if (title.includes('(')) {
                title = title.substring(0, title.indexOf('('));
              }

              return (
                <div className='stat' key={performanceStat.name}>
                  <div className='stat-label'>
                    <div className='performance-title'>
                      <div>{title}</div>
                      {performanceStat.name.includes('gpu') && (
                        <div className='res-selector'>
                          [{selectedGPURes}
                          <span className='res-selector-dropdown'>
                            <button
                              type='button'
                              onClick={toggleResSelectorDropdown}
                            >
                              <Image
                                draggable={false}
                                src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/icon_down_grey.svg?v=1714039727'
                                alt='Select Resolution'
                                width={12}
                                height={12}
                              />
                            </button>
                          </span>
                          ]
                          {showResSelectorDropdown && (
                            <div className='res-selector-dropdown-options'>
                              {gpuRes.map((res) => {
                                const handleUpdateRes = () => {
                                  updateRes(res);
                                  toggleResSelectorDropdown();
                                };
                                return (
                                  <button
                                    key={res}
                                    type='button'
                                    onClick={handleUpdateRes}
                                  >
                                    {res}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className='performance-tips'>
                      <div className='tip_icon' onClick={handleOpenModal}>
                        <Image
                          draggable={false}
                          src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/info_1.png?v=1714033735'
                          alt='performance tip'
                          width={15}
                          height={15}
                        />
                      </div>
                    </div>

                    {tipName === performanceStat.name && (
                      <PcPerformanceTips
                        name={tipName}
                        pcConfigDoc={pcConfigDoc}
                        isTooltipOpen={isTooltipOpen}
                        handleCloseModal={handleCloseModal}
                      />
                    )}
                  </div>

                  <ProgressBar
                    value={performanceStat.value}
                    target={performanceStat.target}
                    parameter={performanceStat.title}
                  />
                </div>
              );
            })}
          </div>
          <div className='pc-model-overview-look-and-feel'>
            <div className='look_feel_title'>
              <div className='performance-title'>
                {' '}
                LOOK &#38; FEEL
                <div className='performance-tips'>
                  <div className='tip_icon' onClick={handleOpenRgbModal}>
                    <Image
                      draggable={false}
                      src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/info_1.png?v=1714033735'
                      alt='performance tip'
                      width={15}
                      height={15}
                    />
                  </div>
                </div>
              </div>
              {tipName === 'rgb' && (
                <PcPerformanceTips
                  name='rgb'
                  pcConfigDoc={pcConfigDoc}
                  isTooltipOpen={isTooltipOpen}
                  handleCloseModal={handleCloseModal}
                />
              )}
            </div>
            <div className='look-feel-options'>
              {Object.keys(LOOK_AND_FEEL).map((key) => (
                <div
                  key={key}
                  className={classNames('look-feel-option', {
                    'look-feel-selected': lookFeelSelected === key,
                    'look-feel-purple': lookFeelPerformance === 'over',
                    'look-feel-orange': lookFeelPerformance === 'under',
                    'look-feel-green': lookFeelPerformance === 'meets',
                  })}
                >
                  {LOOK_AND_FEEL[key]}
                </div>
              ))}

              {target?.score_rgb && (
                <div className='performance-icon'>
                  <button type='button' onClick={handleOpenTip}>
                    <Image
                      draggable={false}
                      src={lookAndFeelIcon}
                      alt='Look and Feel'
                      width={20}
                      height={20}
                    />
                  </button>
                  <PcModelStatTip
                    type={lookFeelPerformance}
                    handleCloseModal={handleCloseTip}
                    isTooltipOpen={isTipOpen}
                    parameter='LOOK & FEEL'
                  />
                </div>
              )}
            </div>
          </div>

          {/* {Object.keys(target).length === 0 && <PcModelSurveyBox />} */}

          <div className='need-help-mobile'>
            <div
              className='need-help-desktop-title'
              onClick={() => setIsOpen(true)}
            >
              {needHelpTitle}
            </div>
            <div
              className='share-icon'
              role='button'
              onClick={toggleShareConfig}
            >
              <div className='save-share-text'>Share</div>
              <Image
                draggable={false}
                src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/icon-share.svg?v=1714039651'
                width={24}
                height={24}
                alt='share'
              />
            </div>
          </div>
        </div>

        <ReactModal
          isOpen={isOverviewExpanded}
          onRequestClose={() => setIsOverviewExpanded(false)}
          className='modal-content'
          overlayClassName='modal-overlay'
          shouldCloseOnOverlayClick
          shouldCloseOnEsc
          preventScroll
          style={{
            overlay: {
              background: 'rgba(255, 255, 255, 0.25)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              position: 'fixed',
              top: 550,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 100,
            },
            content: {
              width: '100%',
              overflow: 'hidden',
              background: 'black',
            },
          }}
        >
          <div className='modal-wrapper modal-helper'>
            <div className='pc-model-overview-summary-mobile'>
              {/* {sale_highlights && (
                <PcModelSaleHighlightsMobile
                  isOverviewExpanded={isOverviewExpanded}
                  setIsOverviewExpanded={setIsOverviewExpanded}
                  sale_highlights={sale_highlights}
                />
              )} */}
              <div className='pc-model-overview-info'>
                <div className='pc-model-overview-mobile-heading'>
                  <a
                    role='button'
                    onClick={() => setIsOverviewExpanded(!isOverviewExpanded)}
                    className='toggle-mobile-details'
                  >
                    <Image
                      draggable={false}
                      src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/ChevronDown.svg?v=1714034100'
                      alt='Hide Performance'
                      width={16}
                      height={16}
                    />
                    {isOverviewExpanded && <span className='performance'>HIDE DETAILS</span>}
                    {/* {isOverviewExpanded
                      ? 'Hide Performance'
                      : 'Show Performance'} */}
                  </a>
                </div>
                {!isOverviewExpanded && (
                  <a
                    className='pc-model-overview-mobile-heading'
                    role='button'
                    onClick={() => setIsOverviewExpanded(!isOverviewExpanded)}
                  >
                    <span className='performance-icon'>
                      {isOverviewExpanded ? (
                        <Image
                          draggable={false}
                          src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/ChevronDown.svg?v=1714034100'
                          alt='Hide Performance'
                          width={16}
                          height={16}
                        />
                      ) : (
                        <Image
                          draggable={false}
                          src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/ChevronUp.svg?v=1714034075'
                          alt='Hide Performance'
                          width={16}
                          height={16}
                        />
                      )}
                    </span>
                    <span className='performance'>Show Performance</span>
                  </a>
                )}
                <div className='pc-model-overview-money'>
                  <span className='pc-model-overview-currency'>
                    <div>
                      {parseFloat(comparePrice) - parseFloat(price) > 0 && (
                        <div className='compare-container'>
                          <div className='compare_price'>
                            <del>
                              ${parseFloat(comparePrice).toLocaleString()}
                            </del>
                          </div>
                          <div className='saved_money'>
                            You save $
                            {parseFloat(comparePrice) - parseFloat(price)}
                          </div>
                        </div>
                      )}
                    </div>
                  </span>
                  <span className='pc-model-overview-amount'>
                    <span>${parseFloat(price).toLocaleString()}</span>
                  </span>
                </div>
              </div>
            </div>

            <div
              className={classNames('overview-mobile', {
                hide: !isOverviewExpanded,
              })}
            >
              {/* {parseFloat(comparePrice) - parseFloat(price) > 0 && (
                <span className="compare-container-mobile">
                  <span className="compare_price">
                    <del>${parseFloat(comparePrice).toLocaleString()}</del>
                  </span>
                  <span className="saved_money">
                    You save ${parseFloat(comparePrice) - parseFloat(price)}
                  </span>
                </span>
              )} */}

              <div className='pc-model-overview-configured-mobile'>
                <div className='pc-model-overview-image'>
                  <Image
                    draggable={false}
                    alt={pcModel?.model_title || 'Image'}
                    src={
                      pcModel?.thumb_image ||
                      'https://cdn.shopify.com/s/files/1/0637/0407/2436/files/image-placeholder.png?v=1714058007'
                    }
                    layout='fill'
                    objectFit='contain'
                  />
                </div>
                <div>
                  {/* <div className="pc-model-overview-header">
                    {doc?.data?.title}
                  </div> */}
                  <div className='pc-model-overview-header'>
                    {pcModel.model_title}&#39;s performance
                  </div>
                  <div>
                    {/* <div className="pc-model-overview-as-configured">
                      AS CONFIGURED
                    </div> */}
                    <PcModelOverviewSpecs productType={productType} />
                  </div>
                </div>
              </div>
              <div className='pc-model-overview-stats'>
                {performanceStats.map((performanceStat) => {
                  if (
                    performanceStat.name.includes('gpu') &&
                    !performanceStat.name.includes(
                      selectedGPURes.replace('p', ''),
                    )
                  )
                    return null;
                  const handleOpenModal = () => {
                    setIsTooltipOpen(true);
                    setTipName(performanceStat.name);
                  };

                  let { title } = performanceStat;
                  if (title.includes('(')) {
                    title = title.substring(0, title.indexOf('('));
                  }

                  return (
                    <div className='stat' key={performanceStat.name}>
                      <div className='stat-label'>
                        <div className='performance-title'>
                          <div>{title}</div>
                          {performanceStat.name.includes('gpu') && (
                            <div className='res-selector'>
                              [{selectedGPURes}
                              <span className='res-selector-dropdown'>
                                <button
                                  type='button'
                                  onClick={toggleResSelectorDropdown}
                                >
                                  <Image
                                    draggable={false}
                                    src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/icon_down_grey.svg?v=1714039727'
                                    alt='Select Resolution'
                                    width={12}
                                    height={12}
                                  />
                                </button>
                              </span>
                              ]
                              {showResSelectorDropdown && (
                                <div className='res-selector-dropdown-options'>
                                  {gpuRes.map((res) => {
                                    const handleUpdateRes = () => {
                                      updateRes(res);
                                      toggleResSelectorDropdown();
                                    };
                                    return (
                                      <button
                                        key={res}
                                        type='button'
                                        onClick={handleUpdateRes}
                                      >
                                        {res}
                                      </button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <div className='performance-tips'>
                          <div className='tip_icon' onClick={handleOpenModal}>
                            <Image
                              draggable={false}
                              src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/info_1.png?v=1714033735'
                              alt='performance tip'
                              width={15}
                              height={15}
                            />
                          </div>
                        </div>

                        {tipName === performanceStat.name && (
                          <PcPerformanceTips
                            name={tipName}
                            pcConfigDoc={pcConfigDoc}
                            isTooltipOpen={isTooltipOpen}
                            handleCloseModal={handleCloseModal}
                          />
                        )}
                      </div>

                      <ProgressBar
                        value={performanceStat.value}
                        target={performanceStat.target}
                        parameter={performanceStat.title}
                      />
                    </div>
                  );
                })}
              </div>
              <div className='pc-model-overview-look-and-feel'>
                <div className='look_feel_title'>
                  <div className='performance-title'>
                    {' '}
                    LOOK &#38; FEEL
                    <div className='performance-tips'>
                      <div className='tip_icon' onClick={handleOpenRgbModal}>
                        <Image
                          draggable={false}
                          src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/info_1.png?v=1714033735'
                          alt='performance tip'
                          width={15}
                          height={15}
                        />
                      </div>
                    </div>
                  </div>
                  {tipName === 'rgb' && (
                    <PcPerformanceTips
                      name='rgb'
                      pcConfigDoc={pcConfigDoc}
                      isTooltipOpen={isTooltipOpen}
                      handleCloseModal={handleCloseModal}
                    />
                  )}
                </div>
                <div className='look-feel-options'>
                  {Object.keys(LOOK_AND_FEEL).map((key) => (
                    <div
                      key={key}
                      className={classNames('look-feel-option', {
                        'look-feel-selected': lookFeelSelected === key,
                        'look-feel-purple': lookFeelPerformance === 'over',
                        'look-feel-orange': lookFeelPerformance === 'under',
                        'look-feel-green': lookFeelPerformance === 'meets',
                      })}
                    >
                      {LOOK_AND_FEEL[key]}
                    </div>
                  ))}
                  {target?.score_rgb && (
                    <div className='performance-icon'>
                      <button type='button' onClick={handleOpenTip}>
                        <Image
                          draggable={false}
                          src={lookAndFeelIcon}
                          alt='Look and Feel'
                          width={20}
                          height={20}
                        />
                      </button>
                      <PcModelStatTip
                        type={lookFeelPerformance}
                        handleCloseModal={handleCloseTip}
                        isTooltipOpen={isTipOpen}
                        parameter='LOOK & FEEL'
                      />
                    </div>
                  )}
                </div>
              </div>
              {/* {Object.keys(target).length === 0 && <PcModelSurveyBox />} */}

              <div className='need-help-mobile'>
                <div
                  className='need-help-desktop-title'
                  onClick={() => setIsOpen(true)}
                >
                  {needHelpTitle}
                </div>
                <div
                  className='share-icon'
                  role='button'
                  onClick={toggleShareConfig}
                >
                  <div className='save-share-text'>Share</div>
                  <Image
                    draggable={false}
                    src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/icon-share.svg?v=1714039651'
                    width={24}
                    height={24}
                    alt='share'
                  />
                </div>
              </div>
            </div>
          </div>
          <div className='steps'>
            <PcModelStepsNext />
          </div>
          <div className='need-help-desktop'>
            <div
              className='need-help-desktop-title'
              onClick={() => setIsOpen(true)}
            >
              {needHelpTitle}
            </div>
            <div
              className='share-icon'
              role='button'
              onClick={toggleShareConfig}
            >
              <div className='save-share-text'>Share</div>
              <Image
                draggable={false}
                src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/icon-share.svg?v=1714039651'
                width={24}
                height={24}
                alt='share'
              />
            </div>
          </div>
        </ReactModal>

        {/* <PcModelOverviewDetails
          title="test"
          isOpen={isOverviewExpanded}
          onClose={() => setIsOverviewExpanded(false)}
          pcConfigDoc=""
        /> */}

        {!isOverviewExpanded && (
          <>
            <div className='steps'>
              <PcModelStepsNext margins />
            </div>
            <div className='need-help-desktop'>
              <div
                className='need-help-desktop-title'
                onClick={() => setIsOpen(true)}
              >
                Talk to an AFTERSHOCK PC Expert!
              </div>
              <div
                className='share-icon'
                role='button'
                onClick={toggleShareConfig}
              >
                <div className='save-share-text'>Share</div>
                <Image
                  draggable={false}
                  src='https://cdn.shopify.com/s/files/1/0637/0407/2436/files/icon-share.svg?v=1714039651'
                  width={24}
                  height={24}
                  alt='share'
                />
              </div>
            </div>
          </>
        )}
      </div>

      <PcModelModalHelp
        title={'Talk to an AFTERSHOCK PC Expert!'}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        pcConfigDoc={pcConfigDoc}
      />
      <ReactModal
        isOpen={showShare}
        onRequestClose={handleClose}
        className='modal-content'
        overlayClassName='modal-overlay'
        shouldCloseOnOverlayClick
        shouldCloseOnEsc
        preventScroll
        style={{
          overlay: {
            background: 'rgba(255, 255, 255, 0.25)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 100,
          },
          content: {
            margin: '10px',
            borderRadius: '2px',
            minWidth: '50vw',
            overflow: 'hidden',
          },
        }}
      >
        <div className='modal-wrapper modal-helper'>
          <div className='modal-header'>
            {/* <div className="modal-header-icon" onClick={handleClose} /> */}
            <div
              className='modal-header-icon'
              onClick={handleClose}
              style={{
                backgroundImage: `url('https://cdn.shopify.com/s/files/1/0637/0407/2436/files/icon-close_5.png?v=1714062936')`,
              }}
              onMouseEnter={(event) => {
                event.target.style.backgroundImage =
                  "url('https://cdn.shopify.com/s/files/1/0637/0407/2436/files/icon-close-hover_1.png?v=1714063075')";
              }}
              onMouseLeave={(event) => {
                event.target.style.backgroundImage =
                  "url('https://cdn.shopify.com/s/files/1/0637/0407/2436/files/icon-close_5.png?v=1714062936')";
              }}
            />
          </div>
          {/* <Scrollbar
            translateContentSizesToHolder
            style={{ padding: '30px 0' }}
          >
          </Scrollbar> */}
          <div className='modal-body'>
            <ShareButtons url={global?.window?.location?.href} />
          </div>
        </div>
      </ReactModal>

      <style jsx>{styles}</style>
      <style jsx global>
        {`
          .pc-model-overview-container .ReactModal__Overlay {
            background: none !important;
          }
          .modal-wrapper.modal-helper {
            width: 100%;
          }
        `}
      </style>
    </div>
  );
};

PcModelOverview.propTypes = {
  productType: PropTypes.string,
};

PcModelOverview.defaultProps = {
  productType: null,
};
export default PcModelOverview;
